/**
 * Sync Handlers — MESSAGE_SYNC, GAP_WARNING, USER_MESSAGE_BROADCAST
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import type { ClientPayloadOf } from "../socketService";
import { useTaskStore, ackTaskSync } from "../taskStore";
import { useFileUrlStore } from "../fileUrlStore";
import { useComputerStore } from "../computerStore";
import { DEFAULT_AGENT, serverDetailsToPendingInteraction } from "../types";
import type {
  Message,
  ContentBlock,
  ToolUseBlock,
  PendingInteraction,
} from "../types";
import type {
  HarnessId,
  AccessLevel,
  LivePreviewPointer,
  OriginChannel,
} from "@bash/shared";
import { ENGINE_SIGN_IN_STATUS } from "@bash/shared";
import {
  findMessageByAnyId,
  findOptimisticUserMessage,
} from "../helpers/findMessage";
import {
  adoptServerRows,
  settleOverlay,
  withoutAdoptedNotices,
} from "../renderTruth";
import { toCanonicalPath } from "@/lib/file-paths";
import { laterSwitchNet, newestSwitchNet } from "@/lib/planTime";
import { rebuildTeammateLanes } from "./teammateHandlers";
import { consumeReconnectAdopt } from "./syncCoordination";

// Gap-warning re-sync cooldown (per task) — a warning that persists across
// consecutive syncs must not turn into a request loop.
const GAP_RESYNC_COOLDOWN_MS = 10_000;
const gapResyncAt = new Map<string, number>();

/**
 * How long this client's own first-hand "I watched the turn end" outranks a
 * sync still claiming 'processing'. Generous next to the sub-second write race
 * it exists for, and bounded so the server wins again well before anything
 * could genuinely diverge (the turn watchdog re-reads at 30s regardless).
 */
export const SEALED_TURN_TRUST_MS = 60_000;

/**
 * How long a task this client minted reads TASK_NOT_FOUND as "its row isn't
 * inserted yet" rather than "not yours". Generous next to the sub-second
 * insert race, and bounded so a task deleted elsewhere later still reads as
 * gone (the no-access screen) instead of a page that never resolves.
 */
export const NEWBORN_NOT_FOUND_GRACE_MS = 60_000;

/** A task this client minted, still inside the grace window. */
function isNewbornHere(task: { mintedHereAt?: number } | undefined): boolean {
  return (
    task?.mintedHereAt !== undefined &&
    Date.now() - task.mintedHereAt < NEWBORN_NOT_FOUND_GRACE_MS
  );
}

/**
 * Stamp `permissionPending` onto the tool_use blocks that durable pending
 * permission rows point at (by toolUseId), so a cold-loaded awaiting task
 * renders its approval card — not just a blocked composer. The paused fold's
 * status gate (`running`/`paused`) is satisfied by rewriting any other status
 * to 'paused' (a synced unresolved tool is a pause by definition). Exported
 * for tests; mutates in place (the caller owns the merged array).
 */
export function stampPendingPermissionBlocks(
  messages: Message[],
  pendings: Array<PendingInteraction | null>
): void {
  const permissionToolUses = new Set(
    pendings
      .filter(
        (p): p is PendingInteraction =>
          p != null && p.type === "permission" && !!p.toolUseId
      )
      .map((p) => p.toolUseId)
  );
  if (permissionToolUses.size === 0) return;
  for (const message of messages) {
    for (const block of message.content) {
      if (block.type !== "tool_use") continue;
      const tool = block as ToolUseBlock;
      if (!permissionToolUses.has(tool.toolUseId)) continue;
      tool.permissionPending = true;
      if (tool.status !== "running" && tool.status !== "paused") {
        tool.status = "paused";
      }
    }
  }
}

/**
 * The stop button's provisional ("Stop not confirmed") card. The client
 * synthesizes one immediately; the server mints the durable one into the turn's
 * content. Structured discriminator, never prose.
 */
function isProvisionalBlock(b: {
  type?: string;
  outcome?: { kind?: string };
}): boolean {
  return b.type === "error" && b.outcome?.kind === "detached";
}

/**
 * `msg` without its provisional card — or null when the card was all it held
 * (branch A commits the client copy as a row of its own).
 */
function withoutProvisional(msg: Message): Message | null {
  if (!msg.content?.some(isProvisionalBlock)) return msg;
  const content = msg.content.filter((b) => !isProvisionalBlock(b));
  return content.length > 0 ? { ...msg, content } : null;
}

export function registerSyncHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  const handleMessageSync = (data: {
    taskId: string;
    messages: Array<{
      id: string;
      role: "user" | "assistant";
      content: ContentBlock[];
      timestamp: string;
      agentSlug: string;
      sdkMessageUuid?: string;
      seq?: number;
    }>;
    title?: string;
    /** Harness runtime (engine) this task is locked to — hydrates the read-only selector. */
    runtime?: HarnessId;
    /** Pinned model id (§4.7) — hydrates the read-only ModelSelector. */
    model?: string;
    /** Agents first (1c): the durable agent this task runs as; null = Bash. */
    agentId?: string | null;
    /** Access level (permissions v2) — hydrates the task-header picker; null = inherit. */
    accessLevel?: AccessLevel | null;
    /** Durable live-preview pointer — reveals the App tab on a cold load. */
    livePreview?: LivePreviewPointer | null;
    /** R-A channel provenance — the door the task was born through; null = app-born. */
    originChannel?: OriginChannel | null;
    /** Engines T5 (tasks.resume_at): paused on a used-up plan until then; null = not waiting. */
    resumeAt?: string | null;
    /** Several accounts (tasks.engine_slot_id): the slot of the task's last Claude Code turn. */
    engineSlotId?: string | null;
    /** tasks.engine_wait: the turn waits on the person's plan sign-in ('engine_connecting'); null = not waiting. */
    engineWait?: string | null;
    lifecycleState?: string;
    pendingInteraction?: Record<string, unknown> | null;
    /**
     * T9 — ALL pending interaction rows for the task (server-authoritative on
     * reconnect). Each row is the same reason-based pendingDetails shape as
     * `pendingInteraction`, plus optional `subagent_id` / `subagent_path` (T2
     * columns). Rows carrying a `subagent_id` rebuild `pendingByLane` so a refresh
     * mid-pause re-renders each subagent's lane; main-agent rows flow through the
     * single `pendingInteraction` above. Absent ⇒ today's single-pending behavior.
     */
    pendingInteractions?: Array<Record<string, unknown>> | null;
    todos?: Array<{
      turnIndex: number;
      todos: Array<{
        content: string;
        status: "pending" | "in_progress" | "completed";
        activeForm?: string;
        // The board (§4.7): the roster handle holding this item. The group array
        // is hydrated wholesale below, so it survives a reload either way; declared
        // here so the reload path's shape does not drift from `TaskTodoGroup`.
        owner?: string;
      }>;
      createdAt: string;
    }>;
    outputs?: Array<{
      id: string;
      title: string;
      url: string;
      type: "link" | "file" | "image";
      category?: string;
      createdAt: string;
      filePath?: string;
      mediaType?: string;
    }>;
    /**
     * P3.4 — per-task monotonic revision. Present on servers that stamp it;
     * absent on legacy servers ⇒ the handler falls back to the length/wall-clock
     * staleness heuristics below (byte-identical to pre-P3.4).
     */
    seq?: number;
    /** Set when the server will NOT vouch for this task — the sync is a
     *  refusal, never an empty board. */
    error?: string;
    errorCode?: string;
    /**
     * Turn reattach rail: this sync is the CATCH-UP after a runner replayed a turn into a
     * successor server. The replayed content was materialized rather than streamed at us, so
     * this payload — not our overlay — is the copy that holds what we missed. Adopt it
     * mid-turn, exactly like a reconnect-marked sync.
     */
    catchUp?: boolean;
  }) => {
    const taskId = data.taskId;
    if (!taskId) return;

    // NOT YET, never NOT YOURS (staging drill 10-01, task 39865d34). A task
    // this client minted moments ago is asked for before its row is inserted,
    // so the refusal only says the row isn't there yet: no verdict, and NO ack.
    // The request stays pending, so the bounded pump re-asks (the belt); the
    // real path is the row arriving on the user's own stream (vouchForOwnTask).
    if (
      data.errorCode === "TASK_NOT_FOUND" &&
      isNewbornHere(taskStore.getState().tasks.get(taskId))
    ) {
      return;
    }

    // A sync landed — whatever queued request asked for it is satisfied.
    ackTaskSync(taskId);

    // An errored sync is UNKNOWN, never truth — applying it as an empty board
    // fabricated a phantom "Resumed Task · Done · 0 steps" page for any
    // foreign or deleted task URL (founder 08-04: a teammate's shared link,
    // rendered as a live composer into a task that isn't yours).
    if (data.error) {
      if (data.errorCode === "TASK_NOT_FOUND") {
        // This user cannot see this task. Mark the entry denied and STAY PUT —
        // the task page renders the no-access screen in place. The first fix
        // bounced through window.location.assign('/home'), which flashed the
        // phantom, ran a full document load, and dragged the user through the
        // gate splash on the way (founder 08-04 round 2: "it went to the task
        // for a bit and took back to home and the loader page is still there").
        // A belief, not a verdict for good: the user's own stream carrying
        // the row (vouchForOwnTask) or a successful sync clears it.
        taskStore.getState()._mutateTasks((tasks) => {
          const curr = tasks.get(taskId);
          if (curr) {
            curr.accessDenied = true;
          }
        });
      }
      return;
    }

    // The server answered with the task: it is theirs. Clear a no-access
    // belief HERE, before any guard below can return early and leave a stale
    // refusal on screen over a task the server just vouched for.
    if (taskStore.getState().tasks.get(taskId)?.accessDenied) {
      taskStore.getState()._mutateTasks((tasks) => {
        const curr = tasks.get(taskId);
        if (curr) curr.accessDenied = false;
      });
    }

    const serverIds = new Set<string>();
    const serverMessages: Message[] = [];
    for (const m of data.messages || []) {
      if (!serverIds.has(m.id)) {
        serverIds.add(m.id);
        // MESSAGE_SYNC payloads are persisted rows from the server — the id
        // is always the DB row id. Stamp a persisted identity so lookups via
        // findMessageByAnyId match on serverId + sdkMessageUuid alike.
        const identity = {
          kind: "persisted" as const,
          serverId: m.id,
          ...(m.sdkMessageUuid ? { sdkMessageUuid: m.sdkMessageUuid } : {}),
        };
        serverMessages.push({
          ...m,
          timestamp: new Date(m.timestamp),
          identity,
          // RENDER_FROM_TRUTH — committed rows carry their seq so ordering is
          // derivable from the row itself.
          ...(typeof m.seq === "number" ? { seq: m.seq } : {}),
        });
      }
    }

    const agentSlug = serverMessages[0]?.agentSlug || DEFAULT_AGENT;

    // Did this payload carry the SERVER's copy of the stop button's provisional
    // card? Read before the reconnect pop below, so a card riding the adopted
    // partial still counts. Drives the client copy's retirement at the merge.
    const serverSentProvisional = serverMessages.some((sm) =>
      sm.content?.some(isProvisionalBlock)
    );

    const existing = taskStore.getState().tasks.get(taskId);

    // ── Stale-lifecycle guard (founder 08-20, staging task 54510a8b) ──────────
    // A MESSAGE_SYNC can be OLDER than what this client already KNOWS: it watched
    // the runner's own AGENT_END seal the turn (`turnSealedAt`), first-hand
    // terminal truth, while the server answered from a task row/cache that hadn't
    // caught up. Honoring that stale 'processing' re-armed the footer loader for
    // 35s after a Stop, and held the just-ended turn's overlay alive beside its
    // own synced copy. Newer local terminal state wins.
    //
    // Narrow by construction: only while we are STILL idle (any send flips the
    // phase off idle, so a real dispatch is never suppressed), and only inside a
    // bounded window — after it the server is authoritative again, so a turn
    // started on another device (whose AGENT_START this tab missed) still lands.
    const sealedRecently =
      existing?.phase === "idle" &&
      existing.turnSealedAt != null &&
      Date.now() - existing.turnSealedAt < SEALED_TURN_TRUST_MS;
    const syncedLifecycle =
      data.lifecycleState === "processing" && sealedRecently
        ? "idle"
        : data.lifecycleState;

    // Only skip sync when BOTH local and server agree the task is actively processing.
    // If server says idle/error but we're stuck in processing (AGENT_END was lost),
    // fall through to recover — this is the only path that can unstick us.
    // (Phase logic — independent of the revision gate below; the live stream owns
    // the UI while processing, so a sync must never clobber it regardless of seq.)
    //
    // Connection-truth exception: a sync the RECONNECT path asked for (one-shot
    // mark) is the opposite case — the local overlay is missing every delta from
    // the blind window and the server's materialized partial is the truth. Adopt
    // it instead of dropping.
    const bothProcessing =
      existing?.phase === "processing" && data.lifecycleState === "processing";
    // Entry-blind (founder 08-23: "the model is not visible, and also the user
    // side prompt"): a RUNNING task entered on a surface with no local row —
    // the live stream flips the shell to 'processing' before the entry sync's
    // round trip lands, so this guard dropped the only payload carrying the
    // ask, the prior turns, and the locked model, for the WHOLE turn. A local
    // copy with ZERO committed rows has nothing the guard could protect —
    // adopt exactly like the reconnect blind-window path.
    //
    // ...and neither has a copy NO sync ever vouched for that holds only USER
    // rows (#3738 review, S3). A composer-born or voice-born task reaches the
    // tab as a zero-row shell, and a follow-up the server wrote is then
    // ANNOUNCED into it (handleUserMessageBroadcast below): that one row made
    // the shell look hydrated, so the task opened showing one bubble and
    // nothing else until the turn ended. Nothing such a copy holds is lost to
    // adoption: an announced row is matched by its own id, a still-optimistic
    // send is matched by its text or retained (adoptServerRows), and a LIVE
    // overlay outranks the payload's older materialization (the pop below).
    // An ASSISTANT row is what that pop could clobber, and a vouched copy
    // already has its history: both keep the drop. A RECONNECT-marked sync is
    // not this case either: there the local overlay is the STALE copy, and the
    // live-overlay rule below would refuse the repaint it exists for.
    const reconnectAdopt = bothProcessing && consumeReconnectAdopt(taskId);
    const enteringBlind =
      bothProcessing &&
      existing !== undefined &&
      (existing.messages.length === 0 ||
        (!reconnectAdopt &&
          !existing.hasSynced &&
          !existing.messages.some((m) => m.role === "assistant")));
    // Reattach catch-up (turn reattach rail §3.8): the server withheld a replay's streaming
    // frames and pushed this instead. Same case as the reconnect mark — the payload is the
    // truth and the overlay is the partial — but the SERVER is what knows it, so it says so
    // on the payload rather than through a mark this tab never set.
    const adoptMidTurn =
      bothProcessing &&
      (reconnectAdopt || enteringBlind || data.catchUp === true);
    if (bothProcessing && !adoptMidTurn) return;

    // Reconnect adoption: the trailing assistant row of a processing payload is
    // the event-buffer materialization of the in-flight turn (server
    // getMessagesAsync), not a committed row. Pop it out of the committed set
    // now — it becomes the streaming overlay below, and it must not be counted
    // by newAssistantArrived or double-rendered in the thread. A processing
    // payload with no trailing assistant row means the buffer had nothing;
    // keep the local overlay rather than clobbering it with nothing.
    let adoptedPartial: Message | null = null;
    if (
      adoptMidTurn &&
      serverMessages[serverMessages.length - 1]?.role === "assistant"
    ) {
      adoptedPartial = serverMessages.pop() ?? null;
      // Entry-blind with a LIVE overlay already flowing: the popped
      // materialization is an older snapshot of the same in-flight turn — the
      // live overlay stays the truth; the pop only keeps the partial out of
      // the committed thread. (Reconnect adoption keeps adopting: there the
      // local overlay is the stale copy.)
      // ...unless this is a CATCH-UP (turn reattach rail §3.8). Then the
      // overlay is not "live" at all: a replayed AGENT_START created it EMPTY
      // moments ago and every delta behind it was withheld, so the payload IS
      // the truth and dropping it leaves the tab with an empty overlay that
      // only the live tail ever appends to — committed, at the END, as a
      // truncated message.
      if (enteringBlind && existing?.streamingMessage && data.catchUp !== true)
        adoptedPartial = null;
    }

    // P3.4 — staleness gate. When BOTH the incoming payload and the stored task
    // carry a revision, the revision is authoritative: adopt iff strictly newer,
    // skip iff older/equal. `seq` bumps on every server-side write to the task's
    // synced snapshot, so this is a deterministic replacement for the length/
    // wall-clock heuristics. When either side lacks `seq` (legacy server, the
    // first sync before we've stored one, or a task hydrated before seq existed),
    // fall back to the exact heuristics below — byte-identical to pre-P3.4.
    // The reconnect-adopt sync bypasses the gate entirely: mid-turn content is
    // newer than any committed revision can reflect (deltas never bump seq).
    const incomingSeq = data.seq;
    const localSeq = existing?.seq;
    // …and so does a catch-up, at any lifecycle. The server built it while the replay's live
    // tail was still held, so it is BY CONSTRUCTION not behind this tab — but the frames it
    // reconciles are withheld deltas, which never bump the task's revision, so an equal seq
    // is the normal case and the gate would drop the one payload carrying what we withheld.
    if (adoptMidTurn || data.catchUp === true) {
      // fall through to the merge
    } else if (
      typeof incomingSeq === "number" &&
      typeof localSeq === "number"
    ) {
      if (incomingSeq <= localSeq) return;
      // Strictly newer ⇒ adopt: fall through to the merge, skipping the legacy
      // staleness guards (a newer revision already means "not stale").
    } else {
      // ---- Legacy staleness heuristics (unchanged; the seq-absent fallback) ----
      // Skip sync when user has a very recent unprocessed message (optimistic push).
      // Socket reconnect fires MESSAGE_SYNC_REQUEST which returns stale messages
      // (before the user's new message). This would overwrite the optimistic push.
      // Time-bound to 10s: after that, the message was likely lost and we need
      // the sync to recover the task from stuck 'waiting' state.
      //
      // EXCEPTION: If server has MORE messages than local, this is a recovery after
      // a network hiccup (agent processed while socket was down) — apply it.
      if (
        existing?.phase === "waiting" &&
        serverMessages.length <= existing.messages.length
      ) {
        const lastMsg = existing.messages[existing.messages.length - 1];
        const ts = lastMsg?.timestamp;
        const sentAt =
          ts instanceof Date
            ? ts.getTime()
            : typeof ts === "string"
              ? new Date(ts).getTime()
              : 0;
        if (sentAt > 0 && Date.now() - sentAt < 10_000) return;
      }

      // Skip sync if task is starting and user sent message within 10s
      // (prevents stale sync from erasing optimistic state)
      // Same exception: if server has more messages, it's a recovery.
      if (
        existing?.phase === "idle" &&
        serverMessages.length <= existing.messages.length
      ) {
        const lastMsg = existing.messages[existing.messages.length - 1];
        const sentAt =
          lastMsg?.timestamp instanceof Date
            ? lastMsg.timestamp.getTime()
            : typeof lastMsg?.timestamp === "string"
              ? new Date(lastMsg.timestamp).getTime()
              : 0;
        if (sentAt > 0 && Date.now() - sentAt < 10_000) return;
      }
    }

    // RENDER_FROM_TRUTH — "the interrupt flush landed" signal: the payload
    // introduced an assistant row this client never saw. Drives both the
    // awaitingEcho drop in adoption and the overlay orphan rule below.
    const newAssistantArrived = serverMessages.some(
      (sm) =>
        sm.role === "assistant" &&
        findMessageByAnyId(existing?.messages ?? [], sm.id) === -1
    );

    // RENDER_FROM_TRUTH — server rows are truth: content authoritative,
    // per-row seq decides order, client-only rows retained glued in place.
    const mergedMessages: Message[] =
      serverMessages.length > 0
        ? adoptServerRows(existing?.messages ?? [], serverMessages, {
            newAssistantArrived,
          })
        : existing?.messages || [];

    const serverPending =
      data.lifecycleState === "awaiting" && data.pendingInteraction
        ? serverDetailsToPendingInteraction(
            taskId,
            (data.pendingInteraction.agentSlug as string) || agentSlug,
            data.pendingInteraction as {
              reason: string;
              [key: string]: unknown;
            }
          )
        : null;

    // Main-agent rows in the pendingInteractions array (no subagent_id) are the
    // full durable ask-queue set, newest-first. Today's server ships the array
    // with subagent rows only (mains ride the singular above) — this path stays
    // dormant until the server includes them, then a refresh mid-multi-pause
    // rebuilds the whole queue instead of just its newest entry.
    const serverMainRows: PendingInteraction[] | null = Array.isArray(
      data.pendingInteractions
    )
      ? data.pendingInteractions
          .filter((row) => !(row.subagent_id ?? row.subagentId))
          .map((row) =>
            serverDetailsToPendingInteraction(
              taskId,
              (row.agentSlug as string) || agentSlug,
              row as { reason: string; [key: string]: unknown }
            )
          )
          .filter((p): p is PendingInteraction => p != null)
          .reverse() // newest-first → FIFO arrival order
      : null;

    // Trust server authority: if server says NOT awaiting, clear pending interaction.
    // Stale local pendingInteraction must not override server-reconciled state (e.g., after
    // sandbox disconnect where reconcileProcessingTasks moved awaiting→idle).
    let resolvedPending: PendingInteraction | null = null;
    let resolvedQueue: PendingInteraction[] = [];
    if (data.lifecycleState === "awaiting") {
      if (serverMainRows?.length) {
        resolvedPending = serverMainRows[0];
        resolvedQueue = serverMainRows.slice(1);
      } else if (serverPending) {
        // Scalar-only payload: the server sends the NEWEST main row. When it
        // corroborates an ask we already hold live, keep our head + queue —
        // the live FIFO order is truer, and the live pending is the fresher
        // copy of the ask. An
        // unknown scalar means our local state is stale — adopt it alone.
        const key = serverPending.interactionId ?? serverPending.requestId;
        const held = [
          existing?.pendingInteraction,
          ...(existing?.pendingQueue ?? []),
        ].filter((p): p is PendingInteraction => p != null);
        if (held.some((p) => (p.interactionId ?? p.requestId) === key)) {
          resolvedPending = existing?.pendingInteraction ?? null;
          resolvedQueue = existing?.pendingQueue ?? [];
        } else {
          resolvedPending = serverPending;
        }
      } else {
        resolvedPending = existing?.pendingInteraction ?? null;
        resolvedQueue = existing?.pendingQueue ?? [];
      }
    }

    // T9 — rebuild the per-lane SUBAGENT pending map from the DB rows. Only rows
    // carrying a `subagent_id` become lanes (main-agent rows ride `resolvedPending`
    // above). When the server doesn't send the list, preserve any lanes we already
    // hold so a partial sync doesn't wipe a live subagent pause. Not awaiting ⇒ {}.
    const serverLanes: Record<string, PendingInteraction> = {};
    if (Array.isArray(data.pendingInteractions)) {
      for (const row of data.pendingInteractions) {
        const sid = (row.subagent_id ?? row.subagentId) as string | undefined;
        if (!sid) continue;
        const pending = serverDetailsToPendingInteraction(
          taskId,
          (row.agentSlug as string) || agentSlug,
          row as { reason: string; [key: string]: unknown }
        );
        if (pending) serverLanes[sid] = pending;
      }
    }
    const resolvedByLane: Record<string, PendingInteraction> =
      data.lifecycleState === "awaiting"
        ? Array.isArray(data.pendingInteractions)
          ? serverLanes
          : (existing?.pendingByLane ?? {})
        : {};

    // HITL cold-load fix (founder-hit 07-11): the permission approval card
    // renders off `permissionPending` on the tool_use BLOCK, and the only
    // writer was the live INTERACTION_REQUESTED handler (and only onto the
    // streaming bubble). A deep-link / reload of an awaiting task therefore
    // showed a blocked composer with NO card — the ask was invisible and
    // could only expire. Stamp the flag onto the synced blocks here, from
    // the same durable rows that block the composer.
    stampPendingPermissionBlocks(mergedMessages, [
      resolvedPending,
      ...resolvedQueue,
      ...Object.values(resolvedByLane),
    ]);

    // The runner's status word. The server keeps one of them, the plan sign-in
    // wait (tasks.engine_wait), so a reload or a task opened after the frame gets
    // it back, and its null ends a wait whose end this tab missed. Any other word
    // (compacting) is live-only and lasts while the turn does. Absent = a server
    // that does not send it: keep what the stream told us.
    const liveStatus = existing?.sdkStatus ?? null;
    const sdkStatus =
      syncedLifecycle !== "processing"
        ? null
        : data.engineWait === undefined
          ? liveStatus
          : (data.engineWait ??
            (liveStatus === ENGINE_SIGN_IN_STATUS ? null : liveStatus));

    taskStore.getState()._mutateTasks((tasks) => {
      const curr = tasks.get(taskId);
      // RENDER_FROM_TRUTH — the overlay dies ONLY when its committed row has
      // arrived (settleOverlay bridges the render key + carries live tool
      // decorations onto it). Lifecycle NEVER kills it: nulling the overlay on
      // a non-processing sync before AGENT_END's commit was the "half output
      // then everything appeared" bug (task 75e23f89).
      // Orphan rule — a turn-ended sync that introduced an assistant row this
      // client never saw means the turn's output arrived under an id we never
      // learned (AGENT_END missed in a reconnect gap): the overlay must die
      // even without an expectedServerId match, or it renders forever as a
      // phantom half-turn below the real row.
      const settled = settleOverlay(
        curr?.streamingMessage || null,
        mergedMessages,
        {
          turnEndedWithNewAssistantRow:
            syncedLifecycle !== "processing" && newAssistantArrived,
        }
      );
      // ⭐ The stop button's PROVISIONAL card exists in two copies by design: the
      // client synthesizes one the instant the user presses Stop, and the server
      // mints the durable, correctly-positioned one into the turn's own content.
      // Exactly one may be on screen, and that has to be STRUCTURAL — retirement
      // on ADOPTION of the server's copy, not on the overlay happening to settle
      // in the same update. Overlay settlement is timing: it needs
      // `syncedLifecycle !== 'processing'`, which past SEALED_TURN_TRUST_MS a
      // still-'processing' server keeps false, so the adopted row rendered its
      // card beside the client's.
      //
      // Adoption is decidable here: `serverIds` is exactly what the payload
      // carried, so the SERVER's copy is the one on those rows and the CLIENT's
      // is whatever provisional block survives anywhere else — the held overlay,
      // or a row branch A committed locally.
      //
      // Scoped to THIS TURN — the rows after the last user message, the same
      // scope branch A's own dedup uses. An earlier turn's card may legitimately
      // be the only copy left (its server row's buffer can have been swept), and
      // this turn's arrival must not take it down with it.
      const overlayAfterSettle = settled.overlay;
      const committedAfterSettle = settled.committed;
      // A notice told mid-turn sits in the overlay; its own persisted row is
      // one line with it, so the overlay's copy steps aside once adopted.
      const overlayForTask = withoutAdoptedNotices(
        serverSentProvisional && overlayAfterSettle
          ? withoutProvisional(overlayAfterSettle)
          : overlayAfterSettle,
        settled.committed
      );
      const lastUserIdx = committedAfterSettle
        .map((msg) => msg.role)
        .lastIndexOf("user");
      const messagesForTask = serverSentProvisional
        ? committedAfterSettle.flatMap((msg, i) =>
            i < lastUserIdx || serverIds.has(msg.id)
              ? [msg]
              : (withoutProvisional(msg) ?? [])
          )
        : committedAfterSettle;
      tasks.set(taskId, {
        taskId,
        // The server vouched for this task — a zero-message deep link may now
        // honestly render its (possibly empty) board instead of quiet loading.
        hasSynced: true,
        // P3.4 — remember the revision we just adopted so the next sync can
        // rev-compare. Absent on the payload ⇒ keep whatever we already held.
        seq: data.seq ?? curr?.seq,
        messages: messagesForTask,
        // Only preserve streamingMessage if genuinely still processing.
        // Reconnect adoption outranks both branches: the popped materialized
        // partial IS the overlay now — later deltas append to it seamlessly
        // and AGENT_END reconciles by the same reserved server row id.
        streamingMessage: adoptedPartial ?? overlayForTask,
        phase:
          curr?.phase === "interrupting"
            ? "interrupting"
            : curr?.phase === "waiting" && syncedLifecycle === "processing"
              ? "waiting"
              : syncedLifecycle === "processing"
                ? "processing"
                : // Recorded-and-quiet: the server stamps 'queued' on EVERY recorded send
                  // and holds it there until AGENT_START (the doorbell wait, a ~90s relay
                  // converge, a workspace-head materialize). The message is genuinely in
                  // flight, so falling through to 'idle' told a reloading user that
                  // nothing was happening — no Starting line, no footer, a live composer.
                  // 'waiting' is precisely what the LIVE send path sets, so a rehydrated
                  // dispatch renders the same honest 'Starting…'. Deliberately NOT
                  // 'scheduled': that task waits on its own clock, not on a runner.
                  syncedLifecycle === "queued"
                  ? "waiting"
                  : syncedLifecycle === "error"
                    ? "idle"
                    : "idle",
        title: data.title || curr?.title || "Resumed Task",
        createdAt: curr?.createdAt || new Date(),
        // MESSAGE_SYNC is a server-driven reconciliation, not a user-meaningful
        // event. Preserve the existing value (set by tasks:list / realtime).
        lastActivityAt: curr?.lastActivityAt || new Date(),
        agentSlug: curr?.agentSlug || agentSlug,
        // Locked engine — server is authoritative; hydrate the read-only
        // selector. Falls back to any value we already had, then claude.
        runtime: data.runtime ?? curr?.runtime ?? "native",
        // Pinned model id (§4.7) — server authoritative; fall back to the value
        // we already had; legacy ⇒ undefined (server resolves via resolveAgentModel).
        model: data.model ?? curr?.model,
        // Agents first (1c): server authoritative (it owns the row). Undefined means a server
        // that does not send the field yet, so keep what we had rather than blanking a face.
        agentId:
          data.agentId !== undefined ? data.agentId : (curr?.agentId ?? null),
        // Access level (permissions v2) — server authoritative; null = inherit.
        accessLevel: data.accessLevel ?? curr?.accessLevel ?? null,
        // Durable live-preview pointer — server authoritative (it owns the row).
        // Undefined means a legacy server that doesn't send the field, so keep
        // whatever we had rather than blanking a working App tab.
        livePreviewPointer:
          data.livePreview !== undefined
            ? data.livePreview
            : (curr?.livePreviewPointer ?? null),
        // R-A channel provenance — server authoritative (it owns the row). A
        // legacy server sends nothing, so keep what we already had rather than
        // blanking a chip the list DTO already told the truth about.
        originChannel: data.originChannel ?? curr?.originChannel,
        // Engines T5 — server authoritative: null says the task is not waiting
        // on its plan; a server that does not send the field keeps what the
        // list or the stream already told us.
        resumeAt:
          data.resumeAt !== undefined
            ? data.resumeAt
            : (curr?.resumeAt ?? null),
        sdkStatus,
        // Several accounts — the same rule: null clears, absent keeps.
        engineSlotId:
          data.engineSlotId !== undefined
            ? data.engineSlotId
            : (curr?.engineSlotId ?? null),
        // The ephemeral frame is NOT re-derived here: a sync replaces the whole
        // TaskState, and a live url+token that survived in memory should survive
        // a sync too (it dies when presence reports hibernating/error, or on
        // preview_closed, not on a reload).
        ...(curr?.livePreview ? { livePreview: curr.livePreview } : {}),
        pendingInteraction: resolvedPending,
        pendingQueue: resolvedQueue,
        pendingByLane: resolvedByLane,
        tokenUsage: curr?.tokenUsage || { input: 0, output: 0 },
        turnSummary: curr?.turnSummary || null,
        turnStartTime: curr?.turnStartTime || null,
        agentStartedAt: curr?.agentStartedAt || null,
        // First-hand terminal knowledge survives the replacement: the sealed
        // guard above read it, and the NEXT stale sync must still find it (a
        // replaced task with no seal re-armed the loader and blinked the turn
        // line's clock at every turn end — measured 08-30).
        turnSealedAt: curr?.turnSealedAt ?? null,
        // Same reason: a sync must not turn a lane that ended on a kill back into one
        // that reads "finished" (C1).
        turnEndedInterrupted: curr?.turnEndedInterrupted ?? false,
        // Several accounts: the switch's net time, the LATER of the newest switched
        // turn's own footer (a reload) and what this client already knew.
        switchNetAt: laterSwitchNet(
          curr?.switchNetAt,
          newestSwitchNet(messagesForTask)
        ),
        outputs: curr?.outputs?.length
          ? curr.outputs
          : (data.outputs || []).map((o) => ({
              ...o,
              filePath: toCanonicalPath(o.filePath ?? `files/${o.title}`),
            })),
        todos: curr?.todos?.length ? curr.todos : data.todos || [],
        promptSuggestion: curr?.promptSuggestion || null,
        // P5.4 Agent Teams — the sync replaces the whole TaskState; carry the
        // client-side teammate registry (hidden-lane marker + parent roster)
        // or a synced teammate lane loses its identity and leaks into lists.
        ...(curr?.teammateMeta ? { teammateMeta: curr.teammateMeta } : {}),
        ...(curr?.teammates ? { teammates: curr.teammates } : {}),
      });
    });

    // A sign-in this tab learns of only now (a reload, a task opened after the
    // frame) is in the box's browser: open the computer there, as the live
    // computer:show did. Once per wait, not on every later sync.
    if (
      sdkStatus === ENGINE_SIGN_IN_STATUS &&
      liveStatus !== ENGINE_SIGN_IN_STATUS
    )
      useComputerStore.getState().showSurface(taskId, "browser");

    // P5.4 Agent Teams reload durability — rebuild the teammate registry from
    // the freshly-loaded messages (delegated tool_result markers) and fetch
    // each teammate thread we don't hold yet. No markers ⇒ no-op.
    rebuildTeammateLanes(taskId, mergedMessages);

    // Pre-populate fileUrlStore from task outputs so images render after refresh
    const outputs = data.outputs || [];
    if (outputs.length > 0) {
      const store = useFileUrlStore.getState();
      for (const o of outputs) {
        if (o.url && o.title) {
          const filesPath = toCanonicalPath(o.filePath ?? `files/${o.title}`);
          if (!store.get(filesPath)) {
            store.set(filesPath, { url: o.url, mediaType: o.mediaType || "" });
          }
        }
      }
    }
  };

  const handleGapWarning = (
    data: ClientPayloadOf<typeof EVENTS.MESSAGE_GAP_WARNING>
  ) => {
    if (data.gapDescription)
      console.warn("[Socket] MESSAGE_GAP_WARNING:", data.gapDescription);
    // Connection-truth: a gap is actionable — re-read instead of just logging.
    // Cooldown-guarded so a warning that persists across syncs can't loop.
    if (!data.taskId) return;
    const now = Date.now();
    const last = gapResyncAt.get(data.taskId) ?? 0;
    if (now - last < GAP_RESYNC_COOLDOWN_MS) return;
    gapResyncAt.set(data.taskId, now);
    socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId: data.taskId });
  };

  const handleUserMessageBroadcast = (
    data: ClientPayloadOf<typeof EVENTS.USER_MESSAGE_BROADCAST>
  ) => {
    const userMessage: Message = {
      id: data.messageId,
      role: "user",
      content: [{ type: "text", text: data.message }],
      timestamp: new Date(data.timestamp),
      agentSlug: DEFAULT_AGENT,
      // data.messageId is the server's DB row id (UUID). The originating tab's
      // optimistic user message was minted with a client `msg-…` id, so a plain
      // id-keyed dedup misses it (≠ the DB UUID) → double render. We reconcile
      // by content below; this persisted identity is what a fresh cross-tab
      // message (no optimistic copy here) gets stamped with.
      identity: { kind: "persisted", serverId: data.messageId },
    };
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;
      // Already present by any id (incl. after the ack reconciled the optimistic
      // copy to the DB UUID) — no-op.
      if (findMessageByAnyId(task.messages, data.messageId) !== -1) return;
      // Race guard: the echo can arrive before sendMessage's ack flips the
      // optimistic user message to persisted. Reconcile that still-optimistic
      // copy in place (stamp the DB serverId) instead of appending a duplicate.
      const optimisticIdx = findOptimisticUserMessage(
        task.messages,
        data.message
      );
      if (optimisticIdx !== -1) {
        const m = task.messages[optimisticIdx];
        m.identity = {
          kind: "persisted",
          serverId: data.messageId,
          ...(m.identity?.kind === "optimistic"
            ? { clientId: m.identity.clientId }
            : {}),
        };
        return;
      }
      task.messages.push(userMessage);
      // Same rule as the typed send (sendMessage.ts): a task that is already
      // mid-turn stays 'processing'. A server-written message (a composer or
      // voice follow-up) is announced to EVERY tab, often while a turn streams;
      // demoting it to 'waiting' read as a pause at AGENT_END and let a sync
      // clobber the live overlay.
      if (task.phase !== "processing") task.phase = "waiting";
    });
  };

  socket.on(EVENTS.MESSAGE_SYNC, handleMessageSync);
  socket.on(EVENTS.MESSAGE_GAP_WARNING, handleGapWarning);
  socket.on(EVENTS.USER_MESSAGE_BROADCAST, handleUserMessageBroadcast);

  return () => {
    socket.off(EVENTS.MESSAGE_SYNC, handleMessageSync);
    socket.off(EVENTS.MESSAGE_GAP_WARNING, handleGapWarning);
    socket.off(EVENTS.USER_MESSAGE_BROADCAST, handleUserMessageBroadcast);
  };
}
